/**
 * Vocabolario del Portale Qualità (QA26-F3.1, docs/modello.md): enum, etichette IT/EN, sezioni del portale, permessi per ruolo.
 * I valori degli enum sono identici agli enum Prisma (backend/prisma/schema.prisma).
 */

export const API_PREFIX = "/api/v1";

export type Testo = { it: string; en: string };

/* ---------- ruoli per sezione e permessi (NCI26-F3.0) ---------- */

/**
 * Profili della sezione Red Flag (manuale, presentazione, Guida). L'admin è il profilo dell'admin di portale. I claim
 * seguono i ruoli della sezione Red Flag.
 */
export const RUOLI = ["admin", "direzione", "commerciale", "qualita", "amministrazione"] as const;
export type Ruolo = (typeof RUOLI)[number];
export const RUOLO_LABEL: Record<Ruolo, Testo> = {
  admin: { it: "Admin", en: "Admin" },
  direzione: { it: "Direzione", en: "Management" },
  commerciale: { it: "Commerciale", en: "Sales" },
  qualita: { it: "Qualità", en: "Quality" },
  amministrazione: { it: "Amministrazione", en: "Accounting" },
};
export const RUOLO_DESCRIZIONE: Record<Ruolo, Testo> = {
  admin: { it: "Tutto, accessi, liste e criteri, registro", en: "Everything, access, lists and criteria, log" },
  direzione: { it: "Legge tutto, cruscotto e report", en: "Reads everything, dashboard and report" },
  commerciale: { it: "Apre e aggiorna segnalazioni, vede NC, non vede i claim", en: "Opens and updates reports, sees NCs, does not see claims" },
  qualita: { it: "Classifica, apre e lavora le NC, assegna owner", en: "Classifies, opens and works NCs, assigns owners" },
  amministrazione: { it: "Gestisce claim, costo della non qualità, blocco pagamento", en: "Manages claims, cost of poor quality, payment hold" },
};

/**
 * Ruoli del portale, uno per sezione e più sezioni per utente (decisione di Mario del 03/11/2026, NCI26-F3.0).
 * Il valore è l'app role Entra: `qa.admin` (admin di portale), `redflag.<ruolo>`, `nc.<ruolo>` (NC interne).
 * I permessi di un utente sono l'unione dei permessi dei suoi ruoli.
 */
export const RUOLO_ADMIN = "qa.admin" as const;
export const RUOLI_PORTALE = [
  RUOLO_ADMIN,
  "redflag.direzione",
  "redflag.commerciale",
  "redflag.qualita",
  "redflag.amministrazione",
  "nc.segnalatore",
  "nc.responsabile",
  "nc.ims",
] as const;
export type RuoloPortale = (typeof RUOLI_PORTALE)[number];

/** Sezioni che hanno ruoli propri (l'admin è di portale). */
export const SEZIONI_RUOLI = ["portale", "redflag", "nc"] as const;
export type SezioneRuoli = (typeof SEZIONI_RUOLI)[number];
export const SEZIONE_RUOLI_LABEL: Record<SezioneRuoli, Testo> = {
  portale: { it: "Portale", en: "Portal" },
  redflag: { it: "Red Flag e Claim", en: "Red Flags and Claims" },
  nc: { it: "NC interne", en: "Internal NCs" },
};
export const sezioneDelRuolo = (r: RuoloPortale): SezioneRuoli => (r === RUOLO_ADMIN ? "portale" : (r.split(".")[0] as SezioneRuoli));

export const RUOLO_PORTALE_LABEL: Record<RuoloPortale, Testo> = {
  "qa.admin": { it: "Admin di portale", en: "Portal admin" },
  "redflag.direzione": RUOLO_LABEL.direzione,
  "redflag.commerciale": RUOLO_LABEL.commerciale,
  "redflag.qualita": RUOLO_LABEL.qualita,
  "redflag.amministrazione": RUOLO_LABEL.amministrazione,
  "nc.segnalatore": { it: "Segnalatore", en: "Reporter" },
  "nc.responsabile": { it: "Responsabile di funzione", en: "Function manager" },
  "nc.ims": { it: "IMS manager", en: "IMS manager" },
};
export const RUOLO_PORTALE_DESCRIZIONE: Record<RuoloPortale, Testo> = {
  "qa.admin": { it: "Tutto il portale, tutte le sezioni, accessi, liste e criteri, registro", en: "The whole portal, every section, access, lists and criteria, log" },
  "redflag.direzione": RUOLO_DESCRIZIONE.direzione,
  "redflag.commerciale": RUOLO_DESCRIZIONE.commerciale,
  "redflag.qualita": RUOLO_DESCRIZIONE.qualita,
  "redflag.amministrazione": RUOLO_DESCRIZIONE.amministrazione,
  "nc.segnalatore": { it: "Segnala una NC interna e segue le sue segnalazioni", en: "Reports an internal NC and follows their reports" },
  "nc.responsabile": { it: "Risponde alle NC della sua funzione, piano azioni e aggiornamenti", en: "Answers the NCs of their function, action plan and updates" },
  "nc.ims": { it: "Tutto il registro NC, riclassifica, verifica di efficacia, mail, impostazioni della sezione", en: "The whole NC register, reclassification, effectiveness check, emails, section settings" },
};

/**
 * App role Entra con un nome storico: `redflag.admin` era l'admin quando il portale aveva una sola sezione e resta
 * riconosciuto come admin di portale, così nessuno perde l'accesso (il gruppo sso-qa ha ancora quel ruolo).
 */
export const ALIAS_RUOLI: Readonly<Record<string, RuoloPortale>> = { "redflag.admin": RUOLO_ADMIN };

/** Normalizza un elenco di ruoli (claim `roles` di Entra, DB): minuscolo, alias, solo i noti, senza doppi, in ordine. */
export function ruoliValidi(roles: unknown): RuoloPortale[] {
  if (!Array.isArray(roles)) return [];
  const set = new Set<string>();
  for (const x of roles) {
    const v = String(x).trim().toLowerCase();
    set.add(Object.hasOwn(ALIAS_RUOLI, v) ? ALIAS_RUOLI[v]! : v);
  }
  return RUOLI_PORTALE.filter((r) => set.has(r));
}

export const AZIONI = [
  "rf.vedi",
  "rf.crea",
  "rf.modifica",
  "nc.vedi",
  "nc.lavora",
  "nc.costi",
  "costi.vedi",
  "claim.vedi",
  "claim.gestisci",
  "fornitore.vedi",
  "report.vedi",
  "proposta.crea",
  "proposta.decidi",
  "nci.segnala",
  "nci.gestisci",
  "nci.registro",
  "nci.ims",
  "admin",
] as const;
export type Azione = (typeof AZIONI)[number];

/**
 * Matrice dei permessi per ruolo del portale. Una sola fonte per backend (autorizzazione), frontend (menu e pulsanti),
 * MCP e Assistente. Red Flag (mockup v2, «Accessi e ruoli»): il commerciale non vede claim, fornitori né il costo della
 * non qualità; solo l'admin decide le proposte; chiunque della sezione propone; il costo lo compilano qualità,
 * amministrazione e admin (`nc.costi`), la direzione lo legge (`costi.vedi`). Le azioni `nc.*` sono le NC della sezione
 * Red Flag (8D leggero); le NC interne di Pier hanno le azioni `nci.*`:
 * - `nci.segnala`: nuova NC interna, le proprie segnalazioni, aggiornare le azioni di cui si è owner;
 * - `nci.gestisci`: le NC della propria funzione (risposta, piano azioni, aggiornamenti);
 * - `nci.registro`: tutto il registro NC e il cruscotto della sezione, in lettura;
 * - `nci.ims`: riclassifica e riassegna, verifica di efficacia, valutazione IMS, mail in uscita, impostazioni della sezione.
 */
export const PERMESSI: Record<RuoloPortale, readonly Azione[]> = {
  "qa.admin": AZIONI,
  "redflag.direzione": ["rf.vedi", "rf.crea", "nc.vedi", "costi.vedi", "claim.vedi", "fornitore.vedi", "report.vedi", "proposta.crea"],
  "redflag.commerciale": ["rf.vedi", "rf.crea", "rf.modifica", "nc.vedi", "report.vedi", "proposta.crea"],
  "redflag.qualita": ["rf.vedi", "rf.crea", "rf.modifica", "nc.vedi", "nc.lavora", "nc.costi", "costi.vedi", "claim.vedi", "fornitore.vedi", "report.vedi", "proposta.crea"],
  "redflag.amministrazione": ["rf.vedi", "rf.crea", "nc.vedi", "nc.costi", "costi.vedi", "claim.vedi", "claim.gestisci", "fornitore.vedi", "report.vedi", "proposta.crea"],
  "nc.segnalatore": ["nci.segnala"],
  "nc.responsabile": ["nci.segnala", "nci.gestisci"],
  "nc.ims": ["nci.segnala", "nci.gestisci", "nci.registro", "nci.ims"],
};

/** Stesso insieme di ruoli, a prescindere da ordine, maiuscole e alias. */
export function stessiRuoli(a: unknown, b: unknown): boolean {
  const x = ruoliValidi(a);
  const y = ruoliValidi(b);
  return x.length === y.length && x.every((r) => y.includes(r));
}

/** Permessi di un utente: unione dei permessi dei suoi ruoli, nell'ordine di AZIONI. */
export function permessiDi(ruoli: readonly RuoloPortale[]): Azione[] {
  const set = new Set<Azione>(ruoli.flatMap((r) => PERMESSI[r] ?? []));
  return AZIONI.filter((a) => set.has(a));
}
/** Chi ha questi ruoli può fare `azione`? Basta un ruolo che la permetta. */
export const puo = (ruoli: readonly RuoloPortale[], azione: Azione) => ruoli.some((r) => PERMESSI[r]?.includes(azione) ?? false);
export const haRuolo = (ruoli: readonly RuoloPortale[], r: RuoloPortale) => ruoli.includes(r);

/** Ruolo del portale che dà un profilo Red Flag: `admin` → `qa.admin`, gli altri `redflag.<profilo>`. */
export const ruoloDelProfilo = (p: Ruolo): RuoloPortale => (p === "admin" ? RUOLO_ADMIN : (`redflag.${p}` as RuoloPortale));
/** Profili del manuale e della presentazione Red Flag che i ruoli danno, in ordine di RUOLI (admin primo). */
export function profiliRf(ruoli: readonly RuoloPortale[]): Ruolo[] {
  return RUOLI.filter((p) => ruoli.includes(ruoloDelProfilo(p)));
}
/** Ordine di precedenza dei profili Red Flag per la presentazione al primo accesso. */
export const PRIORITA_PROFILI: readonly Ruolo[] = ["admin", "qualita", "amministrazione", "direzione", "commerciale"];
export const profiloPrincipale = (ruoli: readonly RuoloPortale[]): Ruolo | null => {
  const p = profiliRf(ruoli);
  return PRIORITA_PROFILI.find((x) => p.includes(x)) ?? null;
};

/* ---------- portale a sezioni ---------- */

export type StatoSezione = "attiva" | "in_arrivo";
export interface Sezione {
  chiave: "redflag" | "claim" | "audit" | "ispezioni";
  sigla: string;
  stato: StatoSezione;
  percorso: string | null;
  nome: Testo;
  descrizione: Testo;
  /** azione che serve per vederla (null = tutti) */
  serve: Azione | null;
}
/** Un portale è un processo, una sezione è un sottoprocesso (decisione di Mario del 02/11/2026). */
export const SEZIONI: readonly Sezione[] = [
  {
    chiave: "redflag",
    sigla: "RF",
    stato: "attiva",
    percorso: "/redflag",
    nome: { it: "Red Flag", en: "Red Flags" },
    descrizione: {
      it: "Segnalazioni dai clienti, classificazione, NC con 8D leggero, azioni nel breve e di lungo termine, report per l'ExCo.",
      en: "Customer reports, classification, NCs with a light 8D, short and long term actions, ExCo report.",
    },
    serve: "rf.vedi",
  },
  {
    chiave: "claim",
    sigla: "CL",
    stato: "attiva",
    percorso: "/claim",
    nome: { it: "Claim fornitori", en: "Supplier claims" },
    descrizione: {
      it: "Il file dell'amministrazione: claim ai fornitori, recupero, blocco dei pagamenti.",
      en: "The accounting file: supplier claims, recovery, payment holds.",
    },
    serve: "claim.vedi",
  },
  {
    chiave: "audit",
    sigla: "AU",
    stato: "in_arrivo",
    percorso: null,
    nome: { it: "Audit e NC fornitori", en: "Supplier audits and NCs" },
    descrizione: {
      it: "Modulo che arriva dalla convergenza di Audit Plan: piano audit, rating, NC da audit.",
      en: "Module coming from Audit Plan: audit plan, rating, audit NCs.",
    },
    serve: null,
  },
  {
    chiave: "ispezioni",
    sigla: "CQ",
    stato: "in_arrivo",
    percorso: null,
    nome: { it: "Controllo qualità e ispezioni", en: "Quality control and inspections" },
    descrizione: {
      it: "Esiti delle ispezioni (Inspectorio, BV), FAIL che aprono una segnalazione.",
      en: "Inspection results (Inspectorio, BV), FAILs that open a report.",
    },
    serve: null,
  },
];

/* ---------- segnalazione (red flag) ---------- */

export const SEVERITA = ["CRITICAL", "HIGH", "MEDIUM", "LOW"] as const;
export type Severita = (typeof SEVERITA)[number];

export const STATI_SEGNALAZIONE = ["nuova", "in_gestione", "da_aggiornare", "chiusa", "persa"] as const;
export type StatoSegnalazione = (typeof STATI_SEGNALAZIONE)[number];
export const STATO_SEGNALAZIONE_LABEL: Record<StatoSegnalazione, Testo> = {
  nuova: { it: "Nuova", en: "New" },
  in_gestione: { it: "In gestione", en: "In progress" },
  da_aggiornare: { it: "Da aggiornare", en: "To be updated" },
  chiusa: { it: "Chiusa", en: "Closed" },
  persa: { it: "Persa", en: "Lost" },
};
/** Stati che contano come «aperti» (cruscotto, home, report). */
export const STATI_APERTI: readonly StatoSegnalazione[] = ["nuova", "in_gestione", "da_aggiornare"];
export const aperta = (s: StatoSegnalazione) => STATI_APERTI.includes(s);
/** Una segnalazione aperta ferma da più di tanti giorni diventa «Da aggiornare». */
export const GIORNI_DA_AGGIORNARE = 7;

export const FLAG_SETTIMANA = ["NEW", "UPD"] as const;
export type FlagSettimana = (typeof FLAG_SETTIMANA)[number];

export const SOCIETA_BASE = ["Brandart", "ABC Lab", "Atelier Archiand"] as const;

export const VALUTE = ["EUR", "USD"] as const;
export type Valuta = (typeof VALUTE)[number];
/** Cambio USD → EUR iniziale, come nel file di Colucci. L'admin lo cambia in Liste e criteri. */
export const CAMBIO_USD_EUR_BASE = 0.92;

/* ---------- fonte, tipo, azioni ISO 9001, allegati, PRO 09 (QA26-F4.9..F4.11) ---------- */

/** Fonti della segnalazione (decisione di Mario del 03/11: Salesforce è una delle fonti). Lista gestita in Liste e criteri. */
export const FONTE_SALESFORCE = "Salesforce";
export const FONTI_BASE = ["Mail", "Telefono o call center", "Commerciale (visita, telefonata)", "Compliance", "Amministrazione", FONTE_SALESFORCE, "Altro"] as const;
/** Fonte delle segnalazioni arrivate dal file settimanale di Colucci. */
export const FONTE_IMPORT = "File red flag (import)";

/** Tipi di segnalazione (QA26-F2.9). Lista gestita in Liste e criteri. */
export const TIPI_SEGNALAZIONE_BASE = ["Reclamo cliente", "Rischio operativo", "Gara persa", "Rischio anticipato"] as const;
/** Proposte di classificazione della notte → tipo di segnalazione (proposta di Leo del 03/11). */
export const TIPO_SEGNALAZIONE_DA_CLASSIFICAZIONE: Partial<Record<TipoClassificazione, string>> = {
  nc_fornitore: "Reclamo cliente",
  nc_interna: "Reclamo cliente",
  commerciale: "Gara persa",
  capacita: "Rischio operativo",
};

/** Azioni ISO 9001 sulla segnalazione: correttiva immediata e preventiva (perché non accada più). */
export const TIPI_AZIONE = ["correttiva", "preventiva"] as const;
export type TipoAzione = (typeof TIPI_AZIONE)[number];
export const TIPO_AZIONE_LABEL: Record<TipoAzione, Testo> = {
  correttiva: { it: "Correttiva immediata", en: "Immediate corrective" },
  preventiva: { it: "Preventiva", en: "Preventive" },
};
export const STATI_AZIONE = ["aperta", "in_corso", "fatta", "verificata"] as const;
export type StatoAzione = (typeof STATI_AZIONE)[number];
export const STATO_AZIONE_LABEL: Record<StatoAzione, Testo> = {
  aperta: { it: "Aperta", en: "Open" },
  in_corso: { it: "In corso", en: "In progress" },
  fatta: { it: "Fatta", en: "Done" },
  verificata: { it: "Verificata", en: "Verified" },
};
/** Azioni ancora da fare (contano per scadute, «Le mie azioni», barra del processo). */
export const STATI_AZIONE_APERTI: readonly StatoAzione[] = ["aperta", "in_corso"];

/** Allegati della segnalazione: mail, PDF, immagini, Office. */
export const ALLEGATO_MAX_BYTES = 20 * 1024 * 1024;
export const ALLEGATI_MAX_PER_INVIO = 10;
export const ALLEGATO_MIME: Record<string, string> = {
  msg: "application/vnd.ms-outlook",
  eml: "message/rfc822",
  pdf: "application/pdf",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  webp: "image/webp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  csv: "text/csv",
};
export const ALLEGATO_EST = Object.keys(ALLEGATO_MIME);
export const TIPI_ALLEGATO = ["mail", "pdf", "immagine", "office", "testo"] as const;
export type TipoAllegato = (typeof TIPI_ALLEGATO)[number];
export function tipoAllegato(nome: string): TipoAllegato | null {
  const ext = (nome.split(".").pop() ?? "").toLowerCase();
  if (!(ext in ALLEGATO_MIME)) return null;
  if (ext === "msg" || ext === "eml") return "mail";
  if (ext === "pdf") return "pdf";
  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) return "immagine";
  if (ext === "txt" || ext === "csv") return "testo";
  return "office";
}

/**
 * PRO 09 Customer care (rev. 3 del 14/09/2020, par. 5.1): presa in carico subito, primo aggiornamento al cliente entro
 * 2 giorni lavorativi, poi almeno ogni 5 (ogni 2 per i clienti VIP) fino alla chiusura, che avviene dopo la verifica col cliente.
 */
export const PRO09 = { primoAggiornamento: 2, ogni: 5, ogniVip: 2 } as const;

/* ---------- classificazione ---------- */

export const TIPI_CLASSIFICAZIONE = ["nc_fornitore", "nc_interna", "commerciale", "capacita", "da_classificare"] as const;
export type TipoClassificazione = (typeof TIPI_CLASSIFICAZIONE)[number];
export const TIPO_CLASSIFICAZIONE_LABEL: Record<TipoClassificazione, Testo> = {
  nc_fornitore: { it: "NC fornitore", en: "Supplier NC" },
  nc_interna: { it: "NC interna", en: "Internal NC" },
  commerciale: { it: "Commerciale (nessuna NC)", en: "Commercial (no NC)" },
  capacita: { it: "Capacità (rischio)", en: "Capacity (risk)" },
  da_classificare: { it: "Da classificare", en: "To be classified" },
};
/** Codici del file di Leo (redflags-*.json): F, I, C, K, ?. */
export const TIPO_DA_CODICE: Record<string, TipoClassificazione> = { F: "nc_fornitore", I: "nc_interna", C: "commerciale", K: "capacita", "?": "da_classificare" };
/** Tipi che aprono una NC quando la classificazione è approvata. */
export const TIPI_CON_NC: Partial<Record<TipoClassificazione, TipoNc>> = { nc_fornitore: "fornitore", nc_interna: "interna" };

/* ---------- NC (8D leggero) ---------- */

export const TIPI_NC = ["fornitore", "interna"] as const;
export type TipoNc = (typeof TIPI_NC)[number];
export const TIPO_NC_LABEL: Record<TipoNc, Testo> = { fornitore: { it: "NC fornitore", en: "Supplier NC" }, interna: { it: "NC interna", en: "Internal NC" } };

export const FASI_NC = ["D1", "D2", "D3", "D4", "D5", "D6", "D7", "D8"] as const;
export type FaseNc = (typeof FASI_NC)[number];
export const FASE_NC_LABEL: Record<FaseNc, Testo> = {
  D1: { it: "Squadra", en: "Team" },
  D2: { it: "Descrizione", en: "Description" },
  D3: { it: "Contenimento · azione nel breve", en: "Containment · short term action" },
  D4: { it: "Causa radice", en: "Root cause" },
  D5: { it: "Azione correttiva · lungo termine", en: "Corrective action · long term" },
  D6: { it: "Attuazione", en: "Implementation" },
  D7: { it: "Prevenzione", en: "Prevention" },
  D8: { it: "Chiusura", en: "Closure" },
};

export const STATI_NC = ["aperta", "chiusa"] as const;
export type StatoNc = (typeof STATI_NC)[number];
export const ESITI_VERIFICA = ["efficace", "non_efficace"] as const;
export type EsitoVerifica = (typeof ESITI_VERIFICA)[number];
export const ESITO_VERIFICA_LABEL: Record<EsitoVerifica, Testo> = {
  efficace: { it: "Efficace", en: "Effective" },
  non_efficace: { it: "Non efficace, riapri", en: "Not effective, reopen" },
};
/** Voci del costo della non qualità (EUR). */
export const VOCI_COSTO = ["costoRilavorazione", "costoAereo", "costoRimborso", "costoSelezione"] as const;
export type VoceCosto = (typeof VOCI_COSTO)[number];
export const VOCE_COSTO_LABEL: Record<VoceCosto, Testo> = {
  costoRilavorazione: { it: "Rilavorazione", en: "Rework" },
  costoAereo: { it: "Trasporto aereo extra", en: "Extra air freight" },
  costoRimborso: { it: "Rimborso / sconto al cliente", en: "Refund / discount to customer" },
  costoSelezione: { it: "Selezione e ispezioni", en: "Sorting and inspections" },
};

/* ---------- claim ---------- */

export const STATI_CLAIM = ["aperto", "notificato", "in_trattativa", "accettato", "contestato", "recuperato"] as const;
export type StatoClaim = (typeof STATI_CLAIM)[number];
export const STATO_CLAIM_LABEL: Record<StatoClaim, Testo> = {
  aperto: { it: "Aperto", en: "Open" },
  notificato: { it: "Notificato al fornitore", en: "Notified to supplier" },
  in_trattativa: { it: "In trattativa", en: "Negotiating" },
  accettato: { it: "Accettato", en: "Accepted" },
  contestato: { it: "Contestato", en: "Disputed" },
  recuperato: { it: "Recuperato", en: "Recovered" },
};
export const STATI_CLAIM_APERTI: readonly StatoClaim[] = ["aperto", "notificato", "in_trattativa", "accettato", "contestato"];
/**
 * Stato del riepilogo di Antonuccio → stato del portale. Proposta di Nico, da confermare con amministrazione (F2.5):
 * lo stato originale resta sul claim (statoOriginale) e si vede nella scheda.
 */
export const STATO_CLAIM_DA_RIEPILOGO: Record<string, StatoClaim> = {
  "in valutazione": "notificato",
  "in trattativa": "in_trattativa",
  accettato: "accettato",
  "in corso": "accettato",
  "da riaddebitare": "aperto",
  contestato: "contestato",
  recuperato: "recuperato",
  aperto: "aperto",
  notificato: "notificato",
};

/* ---------- proposte ---------- */

export const TIPI_PROPOSTA = ["classificazione", "collegamento_claim", "nuova_voce"] as const;
export type TipoProposta = (typeof TIPI_PROPOSTA)[number];
export const TIPO_PROPOSTA_LABEL: Record<TipoProposta, Testo> = {
  classificazione: { it: "Tipo e cause", en: "Type and causes" },
  collegamento_claim: { it: "Collegamento claim", en: "Claim link" },
  nuova_voce: { it: "Nuova voce di lista", en: "New list item" },
};
export const STATI_PROPOSTA = ["da_approvare", "approvata", "modificata", "rifiutata"] as const;
export type StatoProposta = (typeof STATI_PROPOSTA)[number];
export const STATO_PROPOSTA_LABEL: Record<StatoProposta, Testo> = {
  da_approvare: { it: "Da approvare", en: "To approve" },
  approvata: { it: "Approvata", en: "Approved" },
  modificata: { it: "Modificata e approvata", en: "Changed and approved" },
  rifiutata: { it: "Rifiutata", en: "Rejected" },
};
/** Etichetta di chi propone per le proposte generate dall'import della notte. */
export const PROPONENTE_IMPORT = "Import (Leo)";

/* ---------- liste e criteri ---------- */

export const LISTE = ["societa", "fonte", "tipo_segnalazione", "clienti_vip", "causa_cliente", "causa_chi", "funzione_nc", "modalita_recupero", "verifica_dopo"] as const;
export type Lista = (typeof LISTE)[number];
export const LISTA_LABEL: Record<Lista, Testo> = {
  societa: { it: "Società", en: "Company" },
  fonte: { it: "Fonte della segnalazione", en: "Report source" },
  tipo_segnalazione: { it: "Tipo di segnalazione", en: "Report type" },
  clienti_vip: { it: "Clienti VIP (PRO 09: aggiornamento ogni 2 giorni)", en: "VIP customers (PRO 09: update every 2 days)" },
  causa_cliente: { it: "Cosa vede il cliente", en: "What the customer sees" },
  causa_chi: { it: "Chi l'ha causato", en: "Who caused it" },
  funzione_nc: { it: "Funzione (azioni e NC interne)", en: "Function (actions and internal NCs)" },
  modalita_recupero: { it: "Modalità di recupero", en: "Recovery method" },
  verifica_dopo: { it: "Verifica dell'efficacia dopo", en: "Effectiveness check after" },
};
/** Valori iniziali delle liste (mockup v2). */
export const LISTE_BASE: Record<Lista, readonly string[]> = {
  societa: SOCIETA_BASE,
  fonte: FONTI_BASE,
  tipo_segnalazione: TIPI_SEGNALAZIONE_BASE,
  clienti_vip: [],
  causa_cliente: ["Difetto estetico", "Quantità mancante", "Ritardo di consegna", "Non conforme al campione", "Documenti / autorizzazioni", "Prezzo"],
  causa_chi: ["Fornitore", "Pianificazione", "Controllo qualità interno", "R&D / sviluppo", "Logistica", "Commerciale"],
  funzione_nc: ["Qualità", "Procurement", "Pianificazione", "Logistica", "R&D", "Commerciale", "Customer care", "Amministrazione"],
  modalita_recupero: ["Nota di debito", "Trattenuta su fattura", "Riproduzione gratuita", "Riaddebito costi di selezione"],
  verifica_dopo: ["3 consegne", "1 mese", "3 mesi"],
};
/** Criteri di severità approvati (mockup v2). */
export const CRITERI_SEVERITA_BASE: Record<Severita, string> = {
  CRITICAL: "Rischio concreto di perdere il cliente, blocco della linea del cliente, o valore ≥ 100 k€",
  HIGH: "Reclamo formale o costo per Brandart, consegna compromessa",
  MEDIUM: "Difetto gestibile senza impatto sul cliente finale",
  LOW: "Segnalazione preventiva",
};
